import React from "react";
import {Box, Chip, LinearProgress, Paper, Stack, Typography} from "@mui/material";
import {Savings} from "@mui/icons-material";
import type {Supplier360} from "../../supplier360DemoData";
import {formatCurrency, opportunityStatusColor, opportunityStatusLabel} from "../../supplier360Utils";

type Props = { supplier: Supplier360 };

export const OpportunitiesTab: React.FC<Props> = ({supplier}) => {
    if (supplier.opportunities.length === 0) {
        return <Typography color="text.secondary">No opportunities identified for this supplier yet.</Typography>;
    }

    const totalSavings = supplier.opportunities.reduce((sum, o) => sum + o.estimatedSavings, 0);

    return <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Savings color="success"/>
            <Typography variant="body1">
                Total identified savings: <strong>{formatCurrency(totalSavings, supplier.currency)}</strong>
            </Typography>
        </Stack>
        {supplier.opportunities.map(o => (
            <Paper key={o.id} variant="outlined" className="s360-item-card" sx={{p: 2}}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box>
                        <Typography variant="h3" sx={{fontSize: 15}}>{o.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{o.type}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {o.estimatedSavings > 0 &&
                            <Typography variant="body1" fontWeight={700} color="success.main">
                                {formatCurrency(o.estimatedSavings, supplier.currency)}
                            </Typography>
                        }
                        <Chip size="small" color={opportunityStatusColor(o.status)} label={opportunityStatusLabel(o.status)}/>
                    </Stack>
                </Box>
                <Typography variant="body2" mt={1}>{o.description}</Typography>
                <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{minWidth: 70}}>
                        Confidence
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={Math.round(o.confidence * 100)}
                        sx={{flexGrow: 1, height: 6, borderRadius: 3}}
                    />
                    <Typography variant="caption" fontWeight={600}>{Math.round(o.confidence * 100)}%</Typography>
                </Box>
            </Paper>
        ))}
    </Box>;
};
